import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  MoreHorizontal,
  Play,
  CheckCircle,
  CalendarDays,
  CalendarX2,
  Filter,
  Plus,
  UserPlus,
  Edit,
  Trash2,
  Clock as ClockIcon,
  FileText,
  Users,
  Lock,
  Sparkles,
  Download,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import "katex/dist/katex.min.css";
import Modal from "../../components/ui/Modal";
import { CustomVideoPlayer } from "../../components/video/CustomVideoPlayer";
import EditSummaryModal from "../../components/modals/EditSummaryModal";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import type { Meeting, MeetingFilters, MeetingParticipant, MeetAttendanceRecord } from "../../types/meeting";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import { usePanels } from "../../contexts/PanelContext.jsx";
import checkPermission from "../../utils/permission";
import { useModal } from "../../hooks/useModal";
import MeetingStatusFilter, { MeetingStatusFilterType } from "./components/MeetingStatusFilter";

export default function MeetingsPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  const { user } = useAuth();
  const { mainContentRightInsetActive } = usePanels();
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [creatorMap, setCreatorMap] = useState<Record<string, { firstName?: string; lastName?: string }>>({});
  const requestedCreatorsRef = React.useRef<Set<string>>(new Set());

  const [showReWatchModal, setShowReWatchModal] = useState(false);
  const [reWatchData, setReWatchData] = useState({
    title: "",
    url: "",
  });

  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState<{
    title: string;
    overview?: string;
    content?: string;
    docUrl?: string;
    details?: any;
    nextSteps?: any;
    show?: boolean;
    meetingId?: string;
    userId?: string;
  } | null>(null);

  const [showEditSummaryModal, setShowEditSummaryModal] = useState(false);
  const [editingSummary, setEditingSummary] = useState<{
    meetingId: string;
    meetingTitle: string;
    summary?: any;
  } | null>(null);

  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceRecords, setAttendanceRecords] = useState<MeetAttendanceRecord[]>([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [attendanceMeetingTitle, setAttendanceMeetingTitle] = useState<string>("");
  const [attendanceMeetingId, setAttendanceMeetingId] = useState<string>("");
  const [attendanceOwnerId, setAttendanceOwnerId] = useState<string>("");
  const [attendanceProcessing, setAttendanceProcessing] = useState(false);
  const [joiningMeetingId, setJoiningMeetingId] = useState<string | null>(null);

  const [meetingFilterOpen, setMeetingFilterOpen] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [meetingStartDate, setMeetingStartDate] = useState<string | null>(null);
  const [meetingEndDate, setMeetingEndDate] = useState<string | null>(null);
  const [meetingStatus, setMeetingStatus] = useState<MeetingStatusFilterType>("all");

  const canListMeetings = checkPermission("meet_list");
  const canAddMeeting = checkPermission("meet_add");
  const canUpdateMeeting = checkPermission("meet_update");
  const canDeleteMeeting = checkPermission("meet_delete");
  const canEditSummary = checkPermission("meet_summary_edit");
  const canViewAttendance = checkPermission("meet_attendance_view");
  const canTakeAttendance = checkPermission("meet_attendance_take");

  const convertMathNotation = (text: string): string => {
    let converted = text;

    converted = converted.replace(/\\\(([\s\S]+?)\\\)/g, (match, content) => `$${content}$`);
    converted = converted.replace(/\\\\\(([\s\S]+?)\\\\\)/g, (match, content) => `$${content}$`);
    converted = converted.replace(/\\\[([\s\S]+?)\\\]/g, (match, content) => `$$${content}$$`);
    converted = converted.replace(/\\\\\[([\s\S]+?)\\\\\]/g, (match, content) => `$$${content}$$`);

    converted = converted.replace(/\(([^()]*\\[a-zA-Z]+[^()]*)\)/g, (match, content, offset, fullText) => {
      const beforeChar = offset > 0 ? fullText[offset - 1] : " ";
      if (/[a-zA-Z0-9]/.test(beforeChar)) {
        return match;
      }
      if (content.match(/\\[a-zA-Z]+/)) {
        return `$${content}$`;
      }
      return match;
    });

    return converted;
  };

  useEffect(() => {
    const shouldRefresh = localStorage.getItem("meetingListShouldRefresh");
    if (shouldRefresh) {
      localStorage.removeItem("meetingListShouldRefresh");

      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateViewportState = () => {
      const mobile = window.innerWidth < 640;
      setIsMobileViewport(mobile);
      if (mobile) {
        setMeetingFilterOpen(false);
      }
    };

    updateViewportState();
    window.addEventListener("resize", updateViewportState);
    return () => window.removeEventListener("resize", updateViewportState);
  }, []);

  const refreshMeetings = () => {
    window.location.reload();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".dropdown-container")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDropdownToggle = useCallback(
    (meetingId: string) => {
      const newState = dropdownOpen === meetingId ? null : meetingId;
      setDropdownOpen(newState);
    },
    [dropdownOpen]
  );

  const ensureCreatorLoaded = useCallback(
    async (userId?: string) => {
      if (!userId) return;
      if (creatorMap[userId]) return;
      if (requestedCreatorsRef.current.has(userId)) return;
      requestedCreatorsRef.current.add(userId);
      try {
        const user = await ApiService.getUser(userId);
        setCreatorMap((prev) => ({ ...prev, [userId]: { firstName: user?.firstName, lastName: user?.lastName } }));
      } catch (_) { }
    },
    [creatorMap]
  );

  const handleEdit = useCallback(
    async (meeting: Meeting) => {
      setDropdownOpen(null);

      try {
        await ApiService.getMeeting(meeting.id);
        navigate(`/meetings/edit/${meeting.id}`);
      } catch (error: any) {
        if (error.message && error.message.includes("Meet not found")) {
          await showAlert({
            title: "Toplantı Bulunamadı",
            message: "Bu toplantı artık mevcut değil. Sayfa yenilenecek.",
            type: "error",
          });
          window.location.reload();
        } else {
          await showAlert({
            title: "Hata",
            message: "Toplantı bilgileri alınırken bir hata oluştu.",
            type: "error",
          });
        }
      }
    },
    [navigate, showAlert]
  );

  const handleDeleteClick = useCallback(
    async (meeting: Meeting) => {
      setDropdownOpen(null);

      try {
        await ApiService.getMeeting(meeting.id);
      } catch (error: any) {
        if (error.message && error.message.includes("Meet not found")) {
          await showAlert({
            title: "Toplantı Bulunamadı",
            message: "Bu toplantı zaten silinmiş. Sayfa yenilenecek.",
            type: "error",
          });
          window.location.reload();
          return;
        } else {
          await showAlert({
            title: "Hata",
            message: "Toplantı bilgileri alınırken bir hata oluştu.",
            type: "error",
          });
          return;
        }
      }

      const confirmed = await showConfirm({
        title: "Toplantıyı Sil",
        message: `"${meeting.title}" toplantısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
        confirmText: "Sil",
        confirmButtonVariant: "danger",
      });

      if (confirmed) {
        try {
          await ApiService.deleteMeeting(meeting.id);
          window.location.reload();
        } catch (error: any) {
          console.error("Toplantı silme hatası:", error);
          if (error.message && error.message.includes("Meet not found")) {
            await showAlert({
              title: "Toplantı Bulunamadı",
              message: "Bu toplantı zaten silinmiş. Sayfa yenilenecek.",
              type: "error",
            });
            window.location.reload();
          } else {
            await showAlert({
              title: "Hata",
              message: error.message || "Toplantı silinirken hata oluştu.",
              type: "error",
            });
          }
        }
      }
    },
    [showConfirm, showAlert]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const normalizeStatus = (status?: string) => (status || "").toUpperCase();

  const isMeetingLive = (meeting: Meeting) => {
    const status = normalizeStatus(meeting.status);
    if (status === "LIVE") return true;
    if (status && status !== "LIVE") return false;
    const now = new Date();
    const start = new Date(meeting.startDate);
    const end = new Date(meeting.endDate);
    return now >= start && now <= end;
  };

  const isMeetingUpcoming = (meeting: Meeting) => {
    const status = normalizeStatus(meeting.status);
    if (status === "UPCOMING") return true;
    if (status && status !== "UPCOMING") return false;
    const now = new Date();
    const start = new Date(meeting.startDate);
    const diffInMinutes = (start.getTime() - now.getTime()) / (1000 * 60);
    return diffInMinutes > 0 && diffInMinutes <= 5;
  };

  const isMeetingExpired = (meeting: Meeting) => {
    const status = normalizeStatus(meeting.status);
    if (status === "COMPLETED") return true;
    if (status && status !== "COMPLETED") return false;
    const now = new Date();
    const end = new Date(meeting.endDate);
    return now > end;
  };

  const isMeetingPending = (meeting: Meeting) => {
    const status = normalizeStatus(meeting.status);
    if (status === "PLANNED") return true;
    if (status && status !== "PLANNED") return false;
    const now = new Date();
    const start = new Date(meeting.startDate);
    const diffInMinutes = (start.getTime() - now.getTime()) / (1000 * 60);
    return diffInMinutes > 5;
  };

  const fetchAndJoinMeeting = async (meeting: Meeting) => {
    if (joiningMeetingId === meeting.id) return;
    setJoiningMeetingId(meeting.id);
    try {
      const response = await ApiService.joinMeeting(meeting.id);
      const joinUrl = response?.meetLink;
      if (joinUrl && (isMeetingLive(meeting) || isMeetingUpcoming(meeting))) {
        window.open(joinUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error: any) {
      if (error.message && error.message.includes("Meet not found")) {
        await showAlert({
          title: "Toplantı Bulunamadı",
          message: "Bu toplantı artık mevcut değil. Sayfa yenilenecek.",
          type: "error",
        });
        window.location.reload();
      } else {
        await showAlert({
          title: "Hata",
          message: "Toplantıya katılırken bir hata oluştu.",
          type: "error",
        });
      }
    } finally {
      setJoiningMeetingId((current) => (current === meeting.id ? null : current));
    }
  };

  const handleReWatch = (meeting: Meeting) => {
    setReWatchData({
      title: meeting.title,
      url: meeting.reWatchMedia!.url,
    });
    setShowReWatchModal(true);
  };

  const handleShowSummary = (meeting: Meeting) => {
    setSummaryData({
      title: meeting.title,
      overview: meeting.summary?.overview,
      content: meeting.summary?.content,
      docUrl: meeting.summary?.docUrl,
      details: meeting.summary?.details,
      nextSteps: meeting.summary?.nextSteps,
      show: meeting.summary?.show,
      meetingId: meeting.id,
      userId: meeting.userId,
    });
    setShowSummaryModal(true);
  };

  const handleEditSummary = (meeting: Meeting) => {
    setEditingSummary({
      meetingId: meeting.id,
      meetingTitle: meeting.title,
      summary: meeting.summary || { title: "", overview: "", content: "", show: false },
    });
    setShowEditSummaryModal(true);
  };

  const loadAttendance = async (meeting: Meeting) => {
    setAttendanceError(null);
    setAttendanceLoading(true);
    try {
      const resp = await ApiService.getMeetingAttendance(meeting.id);
      const maybeArray = Array.isArray(resp) ? resp : (resp as any)?.data;
      const raw = Array.isArray(maybeArray) ? maybeArray : [];
      const list: MeetAttendanceRecord[] = raw as MeetAttendanceRecord[];
      setAttendanceRecords(list);
    } catch (error: any) {
      setAttendanceError(error?.message || "Yoklama bilgileri alınamadı");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleShowAttendance = (meeting: Meeting) => {
    setAttendanceMeetingTitle(meeting.title);
    setAttendanceMeetingId(meeting.id);
    setAttendanceOwnerId(meeting.userId);
    setShowAttendanceModal(true);
    loadAttendance(meeting);
  };

  const handleAutoFillAttendance = async () => {
    if (!attendanceMeetingId) return;
    setAttendanceProcessing(true);
    setAttendanceError(null);
    try {
      await ApiService.autoFillMeetingAttendance(attendanceMeetingId);
      await loadAttendance({
        id: attendanceMeetingId,
        userId: attendanceOwnerId,
        title: attendanceMeetingTitle,
        description: "",
        startDate: "",
        endDate: "",
        metadata: {},
        createdAt: "",
        updatedAt: "",
      } as any);
    } catch (error: any) {
      setAttendanceError(error?.message || "Otomatik yoklama alınamadı");
    } finally {
      setAttendanceProcessing(false);
    }
  };

  const handleExportAttendance = async () => {
    if (!attendanceMeetingId) return;

    try {
      const blob = await ApiService.exportMeetingAttendance(attendanceMeetingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `${attendanceMeetingTitle || 'toplanti'}_yoklama.xlsx`;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      await showAlert({
        title: 'Başarılı',
        message: 'Yoklama raporu başarıyla indirildi.',
        type: 'success',
      });
    } catch (error: any) {
      await showAlert({
        title: 'Hata',
        message: error?.message || 'Yoklama raporu indirilemedi.',
        type: 'error',
      });
    }
  };

  const handleSummaryUpdated = () => {
    window.location.reload();
  };

  const handleMeetingDateChange = useCallback((startDate: string | null, endDate: string | null) => {
    setMeetingStartDate(startDate);
    setMeetingEndDate(endDate);
  }, []);

  const renderMeetingStatusFilter = useCallback(() => {
    return (
      <MeetingStatusFilter
        startDate={meetingStartDate}
        endDate={meetingEndDate}
        onDateChange={handleMeetingDateChange}
        selectedStatus={meetingStatus}
        onStatusChange={setMeetingStatus}
        onInteraction={() => {
          if (isMobileViewport) {
            setMeetingFilterOpen(false);
          }
        }}
      />
    );
  }, [meetingStartDate, meetingEndDate, meetingStatus, handleMeetingDateChange, isMobileViewport]);

  const renderMeetingItem = useCallback(
    (meeting: Meeting, viewMode: string = "list") => {
      const creatorFirstName = meeting.user?.firstName || creatorMap[meeting.userId]?.firstName || "";
      const creatorLastName = meeting.user?.lastName || creatorMap[meeting.userId]?.lastName || "";
      if (!meeting.user?.firstName && !creatorMap[meeting.userId]) {
        ensureCreatorLoaded(meeting.userId);
      }

      const handleJoin = async (m: Meeting) => {
        const joinUrl = m?.referenceId;
        if (joinUrl && (m.status === "LIVE" || m.status === "UPCOMING" || isMeetingLive(m) || isMeetingUpcoming(m))) {
          fetchAndJoinMeeting(m);
        } else if (!joinUrl) {
          await showAlert({
            title: "Uyarı",
            message: "Katılım bağlantısı bulunamadı.",
            type: "warning",
          });
        } else {
          await showAlert({
            title: "Uyarı",
            message: "Toplantı zamanı henüz gelmedi.",
            type: "warning",
          });
        }
      };
      const statusNorm = normalizeStatus(meeting.status);
      const canShowJoinButton =
        Boolean(meeting?.referenceId) &&
        (statusNorm === "LIVE" || statusNorm === "UPCOMING" || isMeetingLive(meeting) || isMeetingUpcoming(meeting));
      if (viewMode === "grid") {
        return (
          <div
            key={meeting.id}
            className="relative bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-full">
            {(canUpdateMeeting || canDeleteMeeting) && (
              <div className="absolute top-3 right-3 dropdown-container z-20">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownToggle(meeting.id);
                  }}
                  className="p-1.5 rounded-full text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                  title="Diğer İşlemler"
                  aria-label="Diğer işlemler">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                {dropdownOpen === meeting.id && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg z-50 border border-gray-200">
                    <div className="py-1">
                      {canUpdateMeeting && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(meeting);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50">
                          <Edit className="w-4 h-4" />
                          Düzenle
                        </button>
                      )}
                      {canDeleteMeeting && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(meeting);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                          Sil
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="mb-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex-shrink-0">
                  {isMeetingLive(meeting) ? (
                    <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Play className="h-5 w-5 text-red-600 fill-current" />
                    </div>
                  ) : isMeetingUpcoming(meeting) ? (
                    <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-600" />
                    </div>
                  ) : isMeetingExpired(meeting) ? (
                    <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                  ) : (
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <CalendarDays className="h-5 w-5 text-blue-600" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 leading-6 line-clamp-2 break-words">{meeting.title}</h3>
                </div>
              </div>
              <div className="mt-2">
                {isMeetingLive(meeting) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse flex-shrink-0"></div>
                    <span>CANLI</span>
                  </span>
                )}
                {isMeetingUpcoming(meeting) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-sm">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span>YAKLAŞAN</span>
                  </span>
                )}
                {isMeetingExpired(meeting) && !isMeetingLive(meeting) && !isMeetingUpcoming(meeting) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
                    <CheckCircle className="w-3 h-3 flex-shrink-0" />
                    <span>TAMAMLANDI</span>
                  </span>
                )}
                {!isMeetingLive(meeting) && !isMeetingUpcoming(meeting) && !isMeetingExpired(meeting) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm">
                    <CalendarDays className="w-3 h-3 flex-shrink-0" />
                    <span>PLANLI</span>
                  </span>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">{meeting.description}</p>

            <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2">
                <div className="flex items-center gap-1.5 text-gray-700 font-medium mb-0.5">
                  <Calendar className="h-3.5 w-3.5 text-green-600" />
                  <span>Başlangıç</span>
                </div>
                <div className="text-gray-600 leading-4 line-clamp-2">{formatDate(meeting.startDate)}</div>
              </div>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2">
                <div className="flex items-center gap-1.5 text-gray-700 font-medium mb-0.5">
                  <CalendarX2 className="h-3.5 w-3.5 text-red-600" />
                  <span>Bitiş</span>
                </div>
                <div className="text-gray-600 leading-4 line-clamp-2">{formatDate(meeting.endDate)}</div>
              </div>
            </div>

            <div className="mt-auto pt-4 border-t border-gray-100">
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                    {meeting.user?.media?.url ? (
                      <img
                        src={meeting.user.media.url}
                        alt={`${creatorFirstName} ${creatorLastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-[10px] font-medium">
                        {(creatorFirstName || "").charAt(0)}
                        {(creatorLastName || "").charAt(0)}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-gray-700 font-medium truncate">
                      {`${creatorFirstName} ${creatorLastName}`.trim() || "Bilinmiyor"}
                    </div>
                  </div>
                </div>
              </div>

              {meeting.participants && meeting.participants.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center flex-wrap gap-1">
                    <span className="text-xs font-medium text-gray-700 mr-2 flex-shrink-0">Katılımcılar:</span>
                    {meeting.participants.slice(0, 3).map((participant) => (
                      <span
                        key={participant.id}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 max-w-full min-w-0">
                        <span className="truncate">
                          {participant.type === "DEPARTMENT"
                            ? participant.department?.name || "Silinmiş"
                            : `${participant.user?.firstName || ""} ${participant.user?.lastName || ""}`.trim() || "Silinmiş"}
                        </span>
                      </span>
                    ))}
                    {meeting.participants.length > 3 && (
                      <span className="text-xs text-gray-500 flex-shrink-0">+{meeting.participants.length - 3} daha</span>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex gap-2">
                  {meeting.reWatchMedia ? (
                    <button
                      onClick={() => handleReWatch(meeting)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex-1 min-w-0"
                      title="Tekrar İzle">
                      <Play className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">Tekrar İzle</span>
                    </button>
                  ) : (
                    canShowJoinButton && (
                      <button
                        onClick={() => handleJoin(meeting)}
                        disabled={joiningMeetingId === meeting.id}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex-1 min-w-0 disabled:cursor-not-allowed disabled:opacity-60"
                        title="Katıl">
                        <UserPlus className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{joiningMeetingId === meeting.id ? "Katılıyor..." : "Katıl"}</span>
                      </button>
                    )
                  )}
                  {((meeting.summary && (meeting.summary.show || user?.id === meeting.userId)) ||
                    (user?.id === meeting.userId && isMeetingExpired(meeting))) && (
                      <button
                        onClick={() => handleShowSummary(meeting)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex-1 min-w-0"
                        title={meeting.summary ? (user?.id === meeting.userId ? "Özet Düzenle" : "Özet Görüntüle") : "Özet Oluştur"}>
                        <FileText className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">Özet</span>
                      </button>
                    )}
                  {canViewAttendance && isMeetingExpired(meeting) && (
                    <button
                      onClick={() => handleShowAttendance(meeting)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex-1 min-w-0"
                      title="Yoklama">
                      <Users className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">Yoklama</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div key={meeting.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow mb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="flex-shrink-0">
                {isMeetingLive(meeting) ? (
                  <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                    <Play className="h-4 w-4 text-red-600 fill-current" />
                  </div>
                ) : isMeetingUpcoming(meeting) ? (
                  <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </div>
                ) : isMeetingExpired(meeting) ? (
                  <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                ) : (
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <CalendarDays className="h-4 w-4 text-blue-600" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">{meeting.title}</h3>
                <p className="text-sm text-gray-600 mt-1 break-words">{meeting.description}</p>
              </div>
            </div>
            {(canUpdateMeeting || canDeleteMeeting) && (
              <div className="relative dropdown-container flex-shrink-0">
                <button onClick={() => handleDropdownToggle(meeting.id)} className="p-1.5 rounded-full hover:bg-gray-100">
                  <MoreHorizontal className="h-4 w-4" />
                </button>

                {dropdownOpen === meeting.id && (
                  <div className="absolute left-auto right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-[9999] border border-gray-200">
                    <div className="py-1">
                      {canUpdateMeeting && (
                        <button
                          onClick={() => handleEdit(meeting)}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50">
                          <Edit className="w-4 h-4" />
                          Düzenle
                        </button>
                      )}
                      {canDeleteMeeting && (
                        <button
                          onClick={() => handleDeleteClick(meeting)}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                          Sil
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <div className="flex items-center gap-1.5 font-medium mb-0.5">
                <Calendar className="h-4 w-4 text-green-600" />
                <span>Başlangıç</span>
              </div>
              <p className="text-gray-600 break-words">{formatDate(meeting.startDate)}</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <div className="flex items-center gap-1.5 font-medium mb-0.5">
                <CalendarX2 className="h-4 w-4 text-red-600" />
                <span>Bitiş</span>
              </div>
              <p className="text-gray-600 break-words">{formatDate(meeting.endDate)}</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                  {meeting.user?.media?.url ? (
                    <img
                      src={meeting.user.media.url}
                      alt={`${creatorFirstName} ${creatorLastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-[10px] font-medium">
                      {(creatorFirstName || "").charAt(0)}
                      {(creatorLastName || "").charAt(0)}
                    </span>
                  )}
                </div>
                <div className="font-medium break-words whitespace-normal">{`${creatorFirstName} ${creatorLastName}`.trim() || "Bilinmiyor"}</div>
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <div>
              {isMeetingLive(meeting) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse flex-shrink-0"></div>
                  <span>CANLI</span>
                </span>
              )}
              {isMeetingUpcoming(meeting) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-sm">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span>YAKLAŞAN</span>
                </span>
              )}
              {isMeetingExpired(meeting) && !isMeetingLive(meeting) && !isMeetingUpcoming(meeting) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
                  <CheckCircle className="w-3 h-3 flex-shrink-0" />
                  <span>TAMAMLANDI</span>
                </span>
              )}
              {!isMeetingLive(meeting) && !isMeetingUpcoming(meeting) && !isMeetingExpired(meeting) && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm">
                  <CalendarDays className="w-3 h-3 flex-shrink-0" />
                  <span>PLANLI</span>
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {meeting.reWatchMedia ? (
                <button
                  onClick={() => handleReWatch(meeting)}
                  className="h-9 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                  title="Tekrar İzle">
                  <Play className="w-4 h-4 flex-shrink-0" />
                  <span>Tekrar İzle</span>
                </button>
              ) : (
                canShowJoinButton && (
                  <button
                    onClick={() => handleJoin(meeting)}
                    className="h-9 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                    title="Katıl">
                    <UserPlus className="w-4 h-4 flex-shrink-0" />
                    <span>Katıl</span>
                  </button>
                )
              )}
              {((meeting.summary && (meeting.summary.show || user?.id === meeting.userId)) ||
                (user?.id === meeting.userId && isMeetingExpired(meeting))) && (
                <button
                  onClick={() => handleShowSummary(meeting)}
                  className="h-9 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                  title={meeting.summary ? (user?.id === meeting.userId ? "Özet Düzenle" : "Özet Görüntüle") : "Özet Oluştur"}>
                  <FileText className="w-4 h-4 flex-shrink-0" />
                  <span>Özet</span>
                </button>
              )}
              {canViewAttendance && isMeetingExpired(meeting) && (
                <button
                  onClick={() => handleShowAttendance(meeting)}
                  className="h-9 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                  title="Yoklama">
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <span>Yoklama</span>
                </button>
              )}
            </div>
          </div>

          {meeting.participants && meeting.participants.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-start flex-wrap gap-1">
                <span className="text-xs font-medium text-gray-700 mr-2 flex-shrink-0">Katılımcılar:</span>
                {meeting.participants.slice(0, 3).map((dept) => (
                  <span
                    key={dept.id}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 max-w-full min-w-0">
                    <span className="break-words whitespace-normal">
                      {dept.type == "DEPARTMENT"
                        ? (dept.department?.name || "Silinmiş") + " (Departman)"
                        : (`${dept.user?.firstName || ""} ${dept.user?.lastName || ""}`.trim() || "Silinmiş") + " (Kullanıcı)"}
                    </span>
                  </span>
                ))}
                {meeting.participants.length > 3 && <span className="text-xs text-gray-500">+{meeting.participants.length - 3} daha</span>}
              </div>
            </div>
          )}
        </div>
      );
    },
    [dropdownOpen, handleDropdownToggle, handleEdit, handleDeleteClick, formatDate, isMeetingLive, isMeetingUpcoming, joiningMeetingId]
  );

  const handleAIPrompt = () => {
    window.dispatchEvent(new CustomEvent("toggleAIPanel"));
    setTimeout(() => {
      const message = "Bugünkü toplantılarım neler?";
      window.dispatchEvent(new CustomEvent("sendAIMessage", { detail: { message } }));
    }, 300);
  };

  const meetingsActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        <button
          onClick={() => setMeetingFilterOpen((prev) => !prev)}
          className={`${LIST_TOOLBAR_BTN} ${
            meetingFilterOpen ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600" : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          }`}
          title="Filtrele"
          aria-pressed={meetingFilterOpen}>
          <Filter className={LIST_TOOLBAR_ICON} />
          <span>Filtrele</span>
        </button>
        {canAddMeeting && (
          <button
            onClick={() => navigate("/meetings/new", { state: { fromList: true } })}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Toplantı</span>
          </button>
        )}
        <button
          onClick={handleAIPrompt}
          className={`${LIST_TOOLBAR_BTN} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600`}>
          <Sparkles className={LIST_TOOLBAR_ICON} />
          <span>AI'ya Sor</span>
        </button>
      </div>
    ),
    [canAddMeeting, handleAIPrompt, navigate, meetingFilterOpen]
  );

  const buildMeetingsPath = useCallback(() => {
    const params: Record<string, string> = {
      sortBy: "startDate",
      sortOrder: "NEAREST",
    };

    if (meetingStartDate) params.startDate = meetingStartDate;
    if (meetingEndDate) params.endDate = meetingEndDate;
    if (meetingStatus && meetingStatus !== "all") {
      params.status = meetingStatus.toLowerCase();
    }

    const query = new URLSearchParams(params).toString();
    return `meets?${query}`;
  }, [meetingStartDate, meetingEndDate, meetingStatus]);

  const meetingsTab: TabItemProps = useMemo(
    () => ({
      type: "list",
      title: "Tüm Toplantılar",
      path: buildMeetingsPath(),
      actions: meetingsActions,
      isSearch: true,
      showToggle: true,
      gridClassName: mainContentRightInsetActive
        ? "grid grid-cols-1 md:grid-cols-2 gap-3 pb-8 items-stretch auto-rows-fr"
        : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-8 items-stretch auto-rows-fr",
      leftArea: !isMobileViewport && meetingFilterOpen ? renderMeetingStatusFilter : undefined,
      refreshKey: `meetings-${refreshTrigger}-${meetingStartDate || ""}-${meetingEndDate || ""}-${meetingStatus || ""}`,
      item: renderMeetingItem,
    }),
    [
      meetingsActions,
      renderMeetingItem,
      refreshTrigger,
      renderMeetingStatusFilter,
      buildMeetingsPath,
      meetingStartDate,
      meetingEndDate,
      meetingStatus,
      isMobileViewport,
      mainContentRightInsetActive,
    ]
  );

  const tabs = useMemo(() => [meetingsTab], [meetingsTab]);

  return (
    <>
      <TabPage title="Toplantılar" tabs={tabs} />
      {isMobileViewport && meetingFilterOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setMeetingFilterOpen(false)}
            aria-label="Filtre panelini kapat"
          />
          <div className="fixed left-3 right-3 top-[188px] z-50 max-h-[65vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
            {renderMeetingStatusFilter()}
          </div>
        </>
      )}
      <Modal
        isOpen={showAttendanceModal}
        onClose={() => setShowAttendanceModal(false)}
        title={`${attendanceMeetingTitle ? attendanceMeetingTitle + " - " : ""}Toplantı Yoklaması`}
        size="lg">
        {canTakeAttendance && (
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                disabled={attendanceProcessing}
                onClick={handleAutoFillAttendance}
                className={`px-3 py-2 text-sm font-medium rounded-md border flex items-center gap-2 ${attendanceProcessing
                  ? "bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed"
                  : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                  }`}>
                {attendanceProcessing ? (
                  <>
                    <ClockIcon className="w-4 h-4 animate-spin" /> İşleniyor...
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4" /> Yoklama Al
                  </>
                )}
              </button>
              <button
                onClick={handleExportAttendance}
                disabled={attendanceRecords.length === 0}
                className={`px-3 py-2 text-sm font-medium rounded-md border flex items-center gap-2 ${attendanceRecords.length === 0
                  ? "bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed"
                  : "bg-green-600 text-white border-green-600 hover:bg-green-700"
                  }`}>
                <Download className="w-4 h-4" /> Excel Olarak İndir
              </button>
            </div>
            {attendanceRecords.length > 0 && (
              <div className="text-xs text-gray-600">
                Toplam Kayıt: {attendanceRecords.length} | Katılan: {attendanceRecords.filter((r) => r.isPresent).length} | Katılmayan:{" "}
                {attendanceRecords.filter((r) => !r.isPresent).length}
              </div>
            )}
          </div>
        )}
        {attendanceLoading && <div className="py-6 text-center text-sm text-gray-500">Yükleniyor...</div>}
        {attendanceError && !attendanceLoading && <div className="py-4 text-center text-red-600 text-sm">{attendanceError}</div>}
        {!attendanceLoading && !attendanceError && (
          <div className="space-y-4">
            {attendanceRecords.length === 0 && <div className="py-6 text-center text-gray-500 text-sm">Yoklama kaydı bulunamadı.</div>}
            {attendanceRecords.length > 0 && (
              <div className="border rounded-md">
                <div className="max-h-[60vh] overflow-y-auto overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium text-gray-700">Katılımcı</th>
                        <th className="px-3 py-2 font-medium text-gray-700">Departman</th>
                        <th className="px-3 py-2 font-medium text-gray-700">Süre (dk)</th>
                        <th className="px-3 py-2 font-medium text-gray-700">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {attendanceRecords.map((rec) => {
                        const deptName = (rec.metadata as any)?.department?.name || (rec.metadata as any)?.departmentName || "-";
                        return (
                          <tr key={rec.id} className={rec.isPresent ? "" : "bg-red-50"}>
                            <td className="px-3 py-2 text-gray-700">{rec.participantName}</td>
                            <td className="px-3 py-2 text-gray-600 text-xs">{deptName}</td>
                            <td className="px-3 py-2 text-gray-700">{rec.totalDuration}</td>
                            <td className="px-3 py-2">
                              {rec.isPresent ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  Katıldı
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  Katılmadı
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        title={(summaryData?.title ? summaryData.title + " - " : "") + "Toplantı Özeti"}
        size="lg">
        {summaryData ? (
          <div className="space-y-4">
            {summaryData.content ? (
              <div>
                {user?.id === summaryData?.userId && (
                  <div
                    className={`mb-4 p-3 rounded-lg border ${summaryData.show ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"
                      }`}>
                    <div className="flex items-center gap-2">
                      {summaryData.show ? (
                        <>
                          <Users className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700">Bu özet tüm katılımcılar tarafından görülebilir.</span>
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-700">
                            Bu özet sadece toplantı sahibi tarafından görülebilir.
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div className="text-gray-700 break-words max-h-[500px] overflow-auto border rounded-md p-3 bg-gray-50 w-full">
                  <div className="prose prose-sm max-w-none w-full">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeHighlight, rehypeKatex]}
                      components={{
                        h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-xl font-semibold text-gray-900 mt-4 mb-2">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">{children}</h3>,
                        p: ({ children }) => <p className="mb-2 last:mb-0 text-gray-700">{children}</p>,
                        code: ({ children, className }) =>
                          className ? (
                            <code className="bg-gray-800 text-gray-100 px-2 py-1 rounded text-sm block">{children}</code>
                          ) : (
                            <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-sm">{children}</code>
                          ),
                        pre: ({ children }) => (
                          <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-sm mb-2">{children}</pre>
                        ),
                        ul: ({ children }) => <ul className="list-disc ml-6 mb-2 text-gray-700">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal ml-6 mb-2 text-gray-700">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                        strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                        em: ({ children }) => <em className="italic">{children}</em>,
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-2">{children}</blockquote>
                        ),
                        table: ({ children }) => (
                          <div className="overflow-x-auto my-2">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">{children}</table>
                          </div>
                        ),
                        thead: ({ children }) => <thead className="bg-gray-100">{children}</thead>,
                        tbody: ({ children }) => <tbody className="bg-white divide-y divide-gray-200">{children}</tbody>,
                        tr: ({ children }) => <tr>{children}</tr>,
                        th: ({ children }) => (
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">{children}</th>
                        ),
                        td: ({ children }) => <td className="px-4 py-2 text-sm text-gray-700">{children}</td>,
                        a: ({ children, href }) => <span className="text-gray-700 cursor-default">{children}</span>,
                      }}>
                      {convertMathNotation(summaryData.content)}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Bu toplantı için henüz özet oluşturulmamış.</p>
              </div>
            )}
            {canEditSummary && user?.id === summaryData?.userId && (
              <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    if (summaryData?.meetingId) {
                      setEditingSummary({
                        meetingId: summaryData.meetingId,
                        meetingTitle: summaryData.title,
                        summary: {
                          title: summaryData.title,
                          overview: summaryData.overview,
                          content: summaryData.content,
                          details: summaryData.details,
                          nextSteps: summaryData.nextSteps,
                          show: summaryData.show || false,
                        },
                      });
                      setShowSummaryModal(false);
                      setShowEditSummaryModal(true);
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-md transition-colors flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Düzenle
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500">Özet bulunamadı.</div>
        )}
      </Modal>
      <Modal isOpen={showReWatchModal} onClose={() => setShowReWatchModal(false)} title={reWatchData.title} size="xl">
        {reWatchData.url ? (
          <div className="w-full">
            <CustomVideoPlayer src={reWatchData.url} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
            <p className="text-gray-500">Video URL bulunamadı</p>
          </div>
        )}
      </Modal>

      {editingSummary && (
        <EditSummaryModal
          isOpen={showEditSummaryModal}
          onClose={() => {
            setShowEditSummaryModal(false);
            setEditingSummary(null);
          }}
          meetingId={editingSummary.meetingId}
          meetingTitle={editingSummary.meetingTitle}
          initialSummary={editingSummary.summary}
          onSummaryUpdated={handleSummaryUpdated}
        />
      )}
    </>
  );
}
